import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Pagination,
  Button,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import AddIcon from '@mui/icons-material/Add';

const SubCategoryList = ({ onEdit, onAdd }) => {
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadSubCategories();
  }, []);

  const loadSubCategories = () => {
    setLoading(true);
    axios.get('/api/categories/sub_categories/all')
      .then(res => setSubCategories(res.data))
      .catch(err => console.error('Error loading sub-categories:', err))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`/api/categories/sub_categories/${id}`);
        setSubCategories(subCategories.filter(sub => sub.id !== id));
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete sub-category');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredSubCategories = subCategories.filter(subCat =>
    subCat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubCategories = filteredSubCategories.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredSubCategories.length / itemsPerPage);

  return (
    <Box>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: '#424242',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.875rem'
          }}
        >
          <SubdirectoryArrowRightIcon sx={{ fontSize: 18 }} />
          All Sub-Categories ({filteredSubCategories.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              fontSize: '0.8rem',
              width: '180px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                }
              }
            }}
          />
          <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ 
            bgcolor: '#43a047',
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { 
              bgcolor: '#388e3c'
            }
          }}
        >
          Add Sub-Category
        </Button>
        </Box>
      </Box>

      {subCategories.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <Typography color="text.secondary">
            No sub-categories found. Create your first sub-category above.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f5f5f5'
                }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Sub-Category Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Parent Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSubCategories.map((sub) => (
                <TableRow 
                  key={sub.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: 'action.hover'
                    },
                    '& .MuiTableCell-root': {
                      py: 0.75,
                      px: 1.5,
                      fontSize: '0.8rem'
                    }
                  }}
                >
                  <TableCell>{sub.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SubdirectoryArrowRightIcon sx={{ fontSize: '1rem' }} color="action" />
                      <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {sub.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={sub.category_name || 'Unknown'} 
                      size="small"
                      sx={{ 
                        height: '20px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: '#fce4ec',
                        color: '#e91e63'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => onEdit(sub)}
                      size="small"
                      sx={{ 
                        color: '#1976d2',
                        '&:hover': { 
                          bgcolor: '#e3f2fd'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(sub.id, sub.name)}
                      size="small"
                      sx={{ 
                        color: '#d32f2f',
                        ml: 1,
                        '&:hover': { 
                          bgcolor: '#ffebee'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </>
      )}
    </Box>
  );
};

export default SubCategoryList;
